"use client"
import { apiSpecificOffers, apiOfferDetails } from '@/apis/offers';
import { apiExpiredOffers } from '@/apis/user';
import { Offer, OffersOffer, PaginationType } from '@/services/dataTypes';
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { filterOfferBanners, getBannerDimensions, getBaseImageUrl, getMerchantHref } from '@/constants/hooks';
import dynamic from 'next/dynamic';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import OffersListView from './OffersListView';

const Banner = dynamic(() => import('./Banner'), { ssr: false });
const ProductCommentModal = dynamic(() => import('./ProductCommentModal'), { ssr: false });

interface Props {
    initialOffers: OffersOffer[];
    companyId: string;
    merchantId: string;
    awaited_p_id: string;
    mer_slug_type: string;
    mer_slug: string;
    offerBanner: OffersOffer[];
    domain: string;
    merchantName: string;
    pagination: PaginationType;
    ads_campaign: boolean;
}

const LazyLoadingOffers = ({ initialOffers, companyId, merchantId, awaited_p_id, mer_slug_type, mer_slug, offerBanner, domain, merchantName, pagination, ads_campaign }: Props) => {
    const [offers, setOffers] = useState<OffersOffer[]>(initialOffers);
    const [expiredOffers, setExpiredOffers] = useState<OffersOffer[]>([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const observer = useRef<IntersectionObserver | null>(null);
    const [filteredOfferBanners, setFilteredOfferBanners] = useState<OffersOffer[]>([]);
    const bannerDisplayAfter = 2;

    const [activeItem, setActiveItem] = useState<OffersOffer | null>(null);
    const [activeMerchantHref, setActiveMerchantHref] = useState<string>('');
    // Guards against duplicate fetches (e.g. effect re-firing before request resolves)
    const fetchingModalRef = useRef(false);

    const router = useRouter();
    const pathname = usePathname();
    const searchParams = useSearchParams();

    useEffect(() => {
        setOffers(initialOffers);
        fetchExpiredOffers();
    }, [initialOffers]);

    const loadMore = async () => {
        if (loading || !hasMore) return;
        if (page == pagination?.last_page) {
            setHasMore(false);
            return;
        }
        setLoading(true);
        try {
            const nextPage = page + 1;
            const response = await apiSpecificOffers(merchantId, companyId, nextPage);
            if (response.data?.offers.length > 0) {
                setOffers(prev => [...prev, ...response.data.offers]);
                setPage(nextPage);
            } else {
                setHasMore(false);
            }
        } catch (error) {
            console.error('Error loading more offers:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchExpiredOffers = async () => {
        try {
            const response = await apiExpiredOffers(merchantId, companyId);
            setExpiredOffers(response.data?.offers || []);
        } catch (error) { }
    };

    const fetchOfferById = useCallback(async (uniqueId: string) => {
        if (fetchingModalRef.current) return;
        fetchingModalRef.current = true;
        try {
            const res = await apiOfferDetails(uniqueId, companyId);
            const fetchedOffer: Offer | undefined = res?.data;
            if (fetchedOffer) {
                const syntheticItem = {
                    id: (fetchedOffer as any).id,
                    status: 1,
                    offer: fetchedOffer,
                    merchant: (fetchedOffer as any).merchant,
                } as OffersOffer;

                setActiveItem(syntheticItem);
                setActiveMerchantHref(getMerchantHref((fetchedOffer as any).merchant, mer_slug, mer_slug_type));
            }
        } catch (error) {
            console.error('Error fetching offer by id:', error);
        } finally {
            fetchingModalRef.current = false;
        }
    }, [companyId, mer_slug, mer_slug_type]);

    const observerCallback = useCallback(
        (node: any) => {
            if (loading) return;
            if (observer.current) observer.current.disconnect();
            observer.current = new IntersectionObserver((entries) => {
                if (entries[0].isIntersecting && hasMore && !loading) {
                    loadMore();
                }
            }, { threshold: 0.1 });
            if (node) observer.current.observe(node);
        },
        [loading, hasMore, loadMore]
    );

    useEffect(() => {
        if (offerBanner?.length > 0) {
            setFilteredOfferBanners(filterOfferBanners(offerBanner, 300, 600, 50, 150))
        }
    }, [offerBanner])

    useEffect(() => {
        const showParam = searchParams.get('show');
        const pIdParam = searchParams.get('p_id') || awaited_p_id;

        if (showParam === 'true' && pIdParam) {
            const pool = [...offers, ...expiredOffers];
            const match = pool.find(
                (item) => String(item?.offer?.unique_id) === pIdParam || String(item?.offer?.id) === pIdParam
            );
            if (match) {
                setActiveItem(match);
                setActiveMerchantHref(getMerchantHref(match.merchant, mer_slug, mer_slug_type));
            } else if (!activeItem) {
                fetchOfferById(pIdParam);
            }
        }
    }, [searchParams, offers, expiredOffers, awaited_p_id]);

    const handleOfferClick = useCallback((item: OffersOffer, merchantHref: string) => {
        setActiveItem(item);
        setActiveMerchantHref(merchantHref);

        const params = new URLSearchParams(searchParams.toString());
        params.set('show', 'true');
        params.set('p_id', String(item?.offer?.unique_id));
        router.push(`${pathname}?${params.toString()}`, { scroll: false });
    }, [searchParams, pathname, router]);

    const handleCloseModal = useCallback(() => {
        setActiveItem(null);
        setActiveMerchantHref('');

        const params = new URLSearchParams(searchParams.toString());
        params.delete('show');
        params.delete('p_id');
        const query = params.toString();
        router.replace(`${pathname}${query ? `?${query}` : ''}`, { scroll: false });
    }, [searchParams, pathname, router]);


    return (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {offers.map((offer, index) => (
                offer.status === 1 && offer.offer.status === 1 ? (
                    <div key={offer.id} ref={index === offers.length - 1 ? observerCallback : undefined} className="flex flex-col h-full">
                        <OffersListView
                            product={offer}
                            companyId={companyId}
                            mer_slug_type={mer_slug_type}
                            mer_slug={mer_slug}
                            domain={domain}
                            ads_campaign={ads_campaign}
                            onOfferClick={handleOfferClick}
                        />
                        {(index + 1) % bannerDisplayAfter === 0 &&
                            filteredOfferBanners.length > 0 &&
                            filteredOfferBanners[Math.floor(index / bannerDisplayAfter)] && (() => {
                                const banner = filteredOfferBanners[Math.floor(index / bannerDisplayAfter)];
                                const dimension = getBannerDimensions(banner);
                                return (
                                    <div className="my-4 col-span-full" key={`banner-${Math.floor(index / bannerDisplayAfter)}`}>
                                        <Banner data={banner} height={dimension?.height} width={dimension?.width} offerLink={null} domain={domain} mer_slug={mer_slug} slug_type={mer_slug_type} />
                                    </div>
                                );
                            })()
                        }
                    </div>
                ) : null
            ))}

            {loading && (
                <div className="col-span-full flex justify-center items-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                </div>
            )}

            {expiredOffers?.length > 0 && (
                <div className="col-span-full mt-8">
                    <h2 className="text-sm font-black text-slate-500 uppercase tracking-widest mb-5 pb-3 border-b border-dashed border-slate-200 text-center">
                        Old deals & coupons that might still work for {merchantName}
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-stretch">
                        {expiredOffers?.slice(0, 8).map((item, i) => (
                            <OffersListView key={i} product={item} companyId={companyId} mer_slug_type={mer_slug_type} mer_slug={mer_slug} domain={domain} ads_campaign={ads_campaign} onOfferClick={handleOfferClick} />
                        ))}
                    </div>
                </div>
            )}

            {activeItem && (
                <ProductCommentModal
                    product={activeItem.offer as any}
                    merchantName={activeItem?.merchant?.merchant_name || merchantName}
                    merchantLogo={getBaseImageUrl(domain, activeItem?.merchant?.merchant_logo, '')}
                    domain={domain}
                    isOpen={!!activeItem}
                    onClose={handleCloseModal}
                />
            )}
        </div>
    )
}

export default LazyLoadingOffers